import KeycloakProvider from 'next-auth/providers/keycloak';
import type { NextAuthOptions } from 'next-auth';
import type { JWT } from 'next-auth/jwt';
import { KEYCLOAK_CLIENT_ID } from './constants';
import { shouldSkipKeycloakLogout } from './skipKeycloakLogout';

const KEYCLOAK_URL = process.env.AUTH_URL ?? process.env.NEXT_PUBLIC_AUTH_URL ?? '';

// Refresh while the access token is still valid. The session is polled every
// 45s, so the buffer has to be longer than that poll: a check that still sees
// more than 60s left skips refresh, and the next one 45s later still finds a
// live token. Keycloak access tokens are 5 minutes, which is longer than this
// buffer. A shorter lifetime is rejected: extending it would make the app send
// a JWT the backend already considers expired.
const REFRESH_BUFFER_MS = 60_000;

// Extracts exp from the JWT payload — used as fallback when account.expires_at is missing.
// Keycloak always provides expires_at, so this should never trigger in practice.
function getTokenExpMs(accessToken?: string): number | undefined {
  if (!accessToken) return undefined;
  try {
    const payload = JSON.parse(Buffer.from(accessToken.split('.')[1], 'base64url').toString());
    return typeof payload.exp === 'number' ? payload.exp * 1000 : undefined;
  } catch {
    return undefined;
  }
}

// Remaining lifetime of the access token Keycloak just issued. The JWT exp is
// what the backend enforces; expires_in is only the fallback when exp is absent.
function accessTokenLifetimeMs(
  accessToken: string | undefined,
  expiresIn: unknown,
): number | undefined {
  const expMs = getTokenExpMs(accessToken);
  if (expMs !== undefined) {
    return expMs - Date.now();
  }
  if (typeof expiresIn === 'number' && Number.isFinite(expiresIn)) {
    return expiresIn * 1000;
  }
  return undefined;
}

// Server-side deduplication: only one refresh request per user at a time.
// Persists across requests within the same Node.js process, preventing the
// two-tab race condition where both tabs use the same refresh_token simultaneously.
const refreshInProgress = new Map<string, Promise<JWT>>();

async function doRefresh(token: JWT): Promise<JWT> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 10_000);

  try {
    const response = await fetch(`${KEYCLOAK_URL}/realms/myrealm/protocol/openid-connect/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        client_id: KEYCLOAK_CLIENT_ID,
        grant_type: 'refresh_token',
        refresh_token: token.refreshToken as string,
      }),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
    const refreshed = await response.json();

    if (!response.ok) {
      console.error('[next-auth][doRefresh] token refresh failed:', response.status, refreshed);

      // invalid_grant = refresh token is dead (password change, session revoked, used twice)
      // This is permanent — no point retrying. Signal logout.
      if (refreshed?.error === 'invalid_grant') {
        console.error(
          '[next-auth][doRefresh] invalid_grant — refresh token is dead, sub:',
          token.sub,
        );
        return { ...token, error: 'RefreshAccessTokenError' };
      }

      // Other errors (5xx, Keycloak temp unavailable) — treat as transient
      throw new Error(`refresh_http_${response.status}`);
    }

    const lifetimeMs = accessTokenLifetimeMs(refreshed.access_token, refreshed.expires_in);
    if (lifetimeMs === undefined || lifetimeMs <= REFRESH_BUFFER_MS) {
      console.error(
        `[next-auth][doRefresh] access token lifetime ${lifetimeMs ?? 'unknown'}ms is not longer than the ${REFRESH_BUFFER_MS}ms refresh buffer, sub:`,
        token.sub,
      );
      return { ...token, error: 'RefreshAccessTokenError' };
    }

    console.log(
      '[next-auth][doRefresh] refresh OK, new expires_in:',
      refreshed.expires_in,
      'sub:',
      token.sub,
    );
    return {
      ...token,
      accessToken: refreshed.access_token,
      refreshToken: refreshed.refresh_token ?? token.refreshToken,
      idToken: refreshed.id_token ?? token.idToken,
      accessTokenExpires: Date.now() + lifetimeMs,
      error: undefined,
    };
  } catch (e) {
    clearTimeout(timeoutId);
    if ((e as Error).name === 'AbortError') {
      console.error('[next-auth] token refresh timed out after 10s');
    } else {
      console.error('[next-auth] refreshAccessToken error:', e);
    }
    return { ...token, error: 'RefreshAccessTokenError' };
  }
}

async function refreshAccessToken(token: JWT): Promise<JWT> {
  const userId = token.sub as string;

  // Return the same in-flight promise if a refresh is already running for this user.
  // Prevents the two-tab race: both tabs share the result of a single Keycloak request.
  const existing = refreshInProgress.get(userId);
  if (existing) {
    return existing;
  }

  const promise = doRefresh(token).finally(() => {
    refreshInProgress.delete(userId);
  });

  refreshInProgress.set(userId, promise);
  return promise;
}

export const authOptions: NextAuthOptions = {
  secret: process.env.NEXTAUTH_SECRET ?? process.env.AUTH_SECRET,
  providers: [
    KeycloakProvider({
      clientId: KEYCLOAK_CLIENT_ID,
      clientSecret: '',
      issuer: `${KEYCLOAK_URL}/realms/myrealm`,
    }),
  ],
  session: { strategy: 'jwt' },
  pages: { signIn: '/auth-error', error: '/auth-error' },
  events: {
    async signOut({ token }: { token: JWT }) {
      // Impersonation still needs the live Keycloak session. The app cookie is cleared
      // without this logout; the impersonation call itself ends the master session.
      if (shouldSkipKeycloakLogout()) return;
      if (token.idToken) {
        const logoutUrl = new URL(`${KEYCLOAK_URL}/realms/myrealm/protocol/openid-connect/logout`);
        logoutUrl.searchParams.set('id_token_hint', token.idToken as string);
        await fetch(logoutUrl.toString()).catch((e) =>
          console.error('[next-auth] keycloak logout failed:', e),
        );
      }
    },
  },
  callbacks: {
    async jwt({ token, account }) {
      if (account) {
        console.log('[next-auth][jwt] new login, account received, sub:', token.sub);
        return {
          ...token,
          accessToken: account.access_token,
          refreshToken: account.refresh_token,
          idToken: account.id_token,
          // Fallback: read exp from the token itself if expires_at is missing.
          // Prevents accessTokenExpires = 0 which triggers refresh on every request.
          accessTokenExpires: account.expires_at
            ? account.expires_at * 1000
            : (getTokenExpMs(account.access_token) ?? Date.now() + 5 * 60 * 1000),
        };
      }

      const expiresAt = token.accessTokenExpires as number;
      const msLeft = expiresAt - Date.now();

      // 60s is longer than the 45s session poll, so the next poll still sees a live token.
      if (msLeft > REFRESH_BUFFER_MS) {
        console.log(
          `[next-auth][jwt] token valid, expires in ${Math.round(msLeft / 1000)}s, sub:`,
          token.sub,
        );
        return token;
      }

      console.log(`[next-auth][jwt] token expired (msLeft=${msLeft}), refreshing, sub:`, token.sub);
      return refreshAccessToken(token);
    },
    async session({ session, token }) {
      // Do not expose accessToken to the client when session is broken —
      // prevents the UI paradox where header shows user name on the error page
      if (token.error === 'RefreshAccessTokenError') {
        console.warn(
          '[next-auth][session] RefreshAccessTokenError for sub:',
          token.sub,
          '— returning broken session',
        );
        session.accessToken = undefined;
        session.error = token.error;
        return session;
      }
      console.log('[next-auth][session] session OK, sub:', token.sub);
      session.accessToken = token.accessToken as string;
      session.error = token.error as string | undefined;
      return session;
    },
  },
};
